import {
	CustomizationGroupDto,
	customizationIconsByType,
	CustomizationItemDto,
	CustomizationType,
	getCustomizationClassByType
} from '../models/customization/customization.dto';
import { CreativeUnitDto, CreativeUnitWithCustomizationsDto } from '../models/creative-unit/creative-unit.dto';
import { MergeTags } from '../../_core/utils/utils.merge-tags';
import { Evaluate } from '../../_core/utils/utils.evaluate';
import { MergePropertiesUtils } from './merge-properties.utils';
import { CustomizationOptionDto, CustomizationOptionMappingDto } from '../models/customization/customization-option.dto';
import { LayerDto } from '../models/layer/layer.dto';
import { cloneDeep, isEqual, merge } from 'lodash';
import { ObjectUtils } from '../../_core/utils';
import { String } from '../../_core/utils/utils.string';
import { ProjectCartItemGroupDto } from '../../cart/models/project-cart-item-group.dto';
import { VisibilityConditionDto } from '../models/visibility-condition.dto';
import { LayerTextDto } from '../models/layer/layer-text.dto';
import { LayerVideoDto } from '../models/layer/layer-video.dto';

export type MappedCustomization = {
	customizationId: string;
	value: unknown;
	children?: MappedCustomization[];
};
export class CustomizationUtils {
	/**
	 * Recursively flatten all customizations into a single array.
	 */
	public static flattenCustomizations(customizations: CustomizationItemDto[]): CustomizationItemDto[] {
		return customizations.reduce<CustomizationItemDto[]>((acc, customization) => {
			acc.push(customization);

			if (customization.type === CustomizationType.GROUP) {
				acc = acc.concat(this.flattenCustomizations(customization.children));
			}

			return acc;
		}, []);
	}

	public static flattenCustomizationsWithGroupName(customizations: CustomizationItemDto[], groupName?: string): CustomizationItemDto[] {
		return customizations.reduce<CustomizationItemDto[]>((acc, customization) => {
			acc.push({ ...customization, label: customization.label + (groupName ? ` (${groupName})` : '') });

			if (customization.type === CustomizationType.GROUP) {
				acc = acc.concat(this.flattenCustomizationsWithGroupName(customization.children, customization.label));
			}

			return acc;
		}, []);
	}

	public static flattenCustomizationsExcludeGroups(
		customizations: CustomizationItemDto[],
		baseGroup: string = '',
		useLocale: boolean = false
	): CustomizationItemDto[] {
		return customizations.reduce<CustomizationItemDto[]>((acc, customization) => {
			if (customization.type === CustomizationType.GROUP) {
				const label = useLocale ? ObjectUtils.getLocale(customization.label, customization.locale?.label) : customization.label;

				const newParentGroup = baseGroup + (baseGroup ? ' - ' : '') + label;
				acc = acc.concat(this.flattenCustomizationsExcludeGroups(customization.children, newParentGroup, useLocale));
			} else {
				acc.push({
					...customization,
					parentGroup: baseGroup
				} as any);
			}

			return acc;
		}, []);
	}

	/**
	 * Recursively find all customizations and return them in a flat array.
	 * NOTE: This does not return a boolean like a typical find method.
	 */
	public static findAllCustomizations(customizations: CustomizationItemDto[], parentId?: string): CustomizationItemDto[] {
		let foundCustomizations: CustomizationItemDto[] = [];

		customizations?.forEach(customization => {
			// Add in a parent id to be able to trace back to parent groups.
			if (parentId) {
				customization = {
					...customization,
					parentId: parentId
				};
			}

			foundCustomizations.push(customization);

			if ((customization as CustomizationGroupDto).children) {
				foundCustomizations = foundCustomizations.concat(
					this.findAllCustomizations((customization as CustomizationGroupDto).children, customization.id)
				);
			}
		});

		return foundCustomizations;
	}

	/**
	 * Iterate recursively through our customizations and apply a filter condition to them.
	 * Customizations that pass are retained.  You can also flatten the result into one array.
	 */
	public static filterAllCustomizations(
		customizations: CustomizationItemDto[],
		func: Function,
		flatten?: boolean,
		excludeEmptyGroups?: boolean
	): CustomizationItemDto[] {
		let filteredCustomizations: CustomizationItemDto[] = [];

		// console.log('Starting Filter check', customizations, flatten);
		customizations.forEach(customization => {
			// console.log('Filter Checking', customization.label, func(customization));
			const isGroup = customization.type === CustomizationType.GROUP;

			if (func(customization)) {
				if (isGroup && customization.children) {
					const children = this.filterAllCustomizations((customization as CustomizationGroupDto).children, func, flatten);

					if (flatten) {
						// Flatten the children into the main array
						// console.log('Flattening children', children, filteredCustomizations, filteredCustomizations.concat(children));
						filteredCustomizations = filteredCustomizations.concat(children);
					} else {
						// Add the parent with filtered children
						const newCustomization = {
							...customization,
							children: children
						};

						if (excludeEmptyGroups && children.length === 0) {
						} else {
							filteredCustomizations.push(newCustomization);
						}
					}
				} else {
					// No children, just add the customization
					filteredCustomizations.push(customization);
				}
			} else {
				// Well, we don't like this customization, but it may have children we like.
				// Only add them if we're flattening, though.
				if (flatten && isGroup && customization.children) {
					filteredCustomizations = filteredCustomizations.concat(
						this.filterAllCustomizations((customization as CustomizationGroupDto).children, func, flatten)
					);
				}
			}
		});

		return filteredCustomizations;
	}

	public static filterCustomizationsWithinGroups(
		customizations: CustomizationItemDto[],
		func: (x: CustomizationItemDto) => boolean
	): CustomizationItemDto[] {
		const hasChildren = (c: any): c is CustomizationGroupDto =>
			c.type === CustomizationType.GROUP && Array.isArray(c?.children) && c.children.length;

		const filtered: CustomizationItemDto[] = [];

		customizations.forEach(item => {
			const selfPasses = func(item);

			if (!selfPasses && hasChildren(item)) {
				// Recurse first
				const filteredChildren = this.filterAllCustomizations(item.children, func, true);
				const childMatches = filteredChildren.length > 0;

				if (selfPasses || childMatches) {
					const keptParent: CustomizationGroupDto = { ...item, children: filteredChildren };
					filtered.push(keptParent);
				}
			} else {
				if (selfPasses) {
					filtered.push(item);
				}
			}
		});

		return filtered;
	}

	public static mapFilterAllCustomizations(
		customizations: CustomizationItemDto[] = [],
		func: (customization: CustomizationItemDto) => MappedCustomization | null | undefined
	): MappedCustomization[] {
		return customizations.flatMap(customization => {
			const mapped = func(customization);

			if (!mapped) {
				return [];
			}

			const result: MappedCustomization = { ...mapped };
			const originalChildren = (customization as CustomizationGroupDto).children;

			if (originalChildren?.length) {
				result.children = this.mapFilterAllCustomizations(originalChildren, func);
			}

			return [result];
		});
	}

	/**
	 * Iterate recursively through our customizations and apply a map function to them.
	 */
	public static mapAllCustomizations(customizations: CustomizationItemDto[], func: Function) {
		return customizations?.map(customization => {
			const newCustomization = { ...func(customization) };

			if ((newCustomization as CustomizationGroupDto).children) {
				newCustomization.children = this.mapAllCustomizations((newCustomization as CustomizationGroupDto).children, func);
			}

			return newCustomization;
		});
	}

	/**
	 * Recursively walk a tree of customizations and update a single customization by its ID,
	 * returning a new tree with only the necessary nodes copied.
	 */
	public static updateCustomizationById(
		list: CustomizationItemDto[],
		targetId: string,
		updater: (n: CustomizationItemDto) => CustomizationItemDto
	): CustomizationItemDto[] {
		// Walk siblings
		for (let i = 0; i < list.length; i++) {
			const node = list[i];

			// Found the node -> update and return early
			if (node.id === targetId) {
				const updated = updater(node);
				if (updated === node) return list; // no changes
				const next = list.slice(); // copy only this level
				next[i] = updated;
				return next;
			}

			// Try children
			const children = (node as any).children as CustomizationItemDto[] | undefined;
			if (children?.length) {
				const nextChildren = this.updateCustomizationById(children, targetId, updater);
				if (nextChildren !== children) {
					const next = list.slice(); // copy only this level
					next[i] = { ...node, children: nextChildren } as any; // copy only along the path
					return next; // EARLY RETURN: nothing else changed
				}
			}
		}
		return list; // not found
	}

	/**
	 * Async version of mapAllCustomizations
	 */
	public static async mapAllCustomizationsAsync(
		customizations: CustomizationItemDto[],
		func: (customization: CustomizationItemDto) => Promise<CustomizationItemDto>
	): Promise<CustomizationItemDto[]> {
		if (!customizations) return [];

		const mapped = await Promise.all(
			customizations.map(async customization => {
				const newCustomization = { ...(await func(customization)) };

				if ((newCustomization as CustomizationGroupDto).children) {
					newCustomization.children = await this.mapAllCustomizationsAsync(
						(newCustomization as CustomizationGroupDto).children,
						func
					);
				}

				return newCustomization;
			})
		);

		return mapped;
	}

	/**
	 * Recursively map all customizations of an array of customizations with a mapping function, adjusting the ID based on nesting as we go.
	 */
	public static mapAllCustomizationsWithIdNesting(customizations: CustomizationItemDto[], func: Function, id?: string) {
		if (!id) {
			id = new Date().getTime().toString();
		}

		return (customizations || [])?.map((customization, index) => {
			const customizationId = id + (index + 1);

			const newCustomization = { ...func(customization), id: customizationId };

			if ((newCustomization as CustomizationGroupDto).children) {
				newCustomization.children = this.mapAllCustomizationsWithIdNesting(
					(newCustomization as CustomizationGroupDto).children,
					func,
					customizationId
				);
			}

			return newCustomization;
		});
	}

	/**
	 * Iterate recursively through our package customizations and override them with the unit customizations.
	 */
	public static overrideCustomizations(
		packageCustomizations: CustomizationItemDto[],
		unitCustomizations: CustomizationItemDto[]
	): CustomizationItemDto[] {
		return this.mapAllCustomizations(packageCustomizations, (customization: CustomizationItemDto) => {
			const creativeUnitCustomization = unitCustomizations?.find(item => item.id === customization.id);

			return creativeUnitCustomization || customization;
		});
	}

	/**
	 * Move a customization up or down in the list of customizations.
	 * Customizations are moved within their parent group, or if they are not found, they are moved within the root.
	 */
	public static moveCustomization(
		customizations: CustomizationItemDto[],
		movedCustomization: CustomizationItemDto,
		direction: -1 | 1
	): CustomizationItemDto[] {
		let newCustomization: CustomizationItemDto[] = [...customizations];
		const customizationIndex = newCustomization.findIndex(c => c.id === movedCustomization.id);

		// is the moved customization currently at the root level?
		if (customizationIndex > -1) {
			const newIndex = customizationIndex + direction;

			if (newIndex >= 0 && newIndex < newCustomization.length) {
				[newCustomization[customizationIndex], newCustomization[newIndex]] = [
					newCustomization[newIndex],
					newCustomization[customizationIndex]
				];
			}
		} else {
			// if not, then recurse through the children until we find the customization
			newCustomization.forEach(c => {
				if (c.type === CustomizationType.GROUP && c.children) {
					c.children = this.moveCustomization(c.children, movedCustomization, direction);
				}
			});
		}

		return newCustomization;
	}

	/**
	 * Looks through the visibility conditions of a customization and determines
	 * whether this customization should be visible to the end user based on the current conditions.
	 */
	public static isCustomizationVisible(
		customization: CustomizationItemDto,
		unit: CreativeUnitWithCustomizationsDto,
		additionalMergeState?: { [key: string]: any },
		activeGroup?: ProjectCartItemGroupDto
	): boolean {
		// The unit's merge state is expensive to build (it applies merge tags to
		// every variable and re-evaluates every customization's conditions), so
		// callers checking a whole list should build it once and use
		// `isCustomizationVisibleWithState` (#11024). Passed as a provider so the
		// build is skipped entirely for a customization the cheap gates below
		// already decide — most rows carry no visibility conditions at all.
		return this.isCustomizationVisibleWithState(
			customization,
			() => (unit ? MergePropertiesUtils.getMergeableProperties(undefined, undefined, unit, activeGroup) : undefined),
			additionalMergeState
		);
	}

	/**
	 * `isCustomizationVisible` with the unit's merge state precomputed —
	 * `unitState` is what `MergePropertiesUtils.getMergeableProperties(undefined,
	 * undefined, unit, activeGroup)` returns, or undefined when there is no unit.
	 * It may also be a provider returning that state, which is only invoked once
	 * the conditions actually have to be evaluated.
	 * Semantics are identical: the DIMENSIONS guard still reads only the
	 * caller-supplied `additionalMergeState`, and `additionalMergeState` still
	 * overlays the unit state.
	 */
	public static isCustomizationVisibleWithState(
		customization: CustomizationItemDto,
		unitState?: { [key: string]: any } | (() => { [key: string]: any } | undefined),
		additionalMergeState?: { [key: string]: any }
	): boolean {
		if (customization.type === CustomizationType.DIMENSIONS && !additionalMergeState?.activeCreativeUnit?.id) {
			return false;
		}

		if (!this.areVisibilityConditionsPresent(customization.visibilityConditions)) {
			return true;
		}

		let state = {};

		// Only now — past the two gates above — is the unit state actually needed.
		const resolvedUnitState = typeof unitState === 'function' ? unitState() : unitState;

		if (resolvedUnitState) {
			state = {
				...state,
				...resolvedUnitState
			};
		}

		if (additionalMergeState) {
			state = {
				...state,
				...(additionalMergeState || {})
			};
		}

		// if (customization.id === '1707157036640') {
		// 	console.log('Checking visibility conditions', customization, state, unit, additionalMergeState);
		// }

		let visible = true;

		// If visibilityConditions are not an array, wrap into an array.
		let conditions = customization.visibilityConditions;
		if (!Array.isArray(conditions)) {
			conditions = [conditions];
		}

		let mergedConditions;
		// Apply merge tags to the condition.
		try {
			mergedConditions = JSON.parse(MergeTags.applyMergeTagsToString(JSON.stringify(conditions), state));
		} catch (e) {
			console.warn('Unable to parse condition merge tags');
		}

		if (mergedConditions) {
			visible = Evaluate.evaluateMultiple(mergedConditions, state);
		}

		// for (let condition of conditions) {
		// 	if (customization.id === '1707157036640') {
		// 		console.log('Checking condition', condition, Evaluate.evaluate(condition, state), state);
		// 	}

		// 	// Apply merge tags to the condition.
		// 	try {
		// 		condition = JSON.parse(MergeTags.applyMergeTagsToString(JSON.stringify(condition), state));
		// 	} catch (e) {
		// 		console.warn('Unable to parse condition merge tags');
		// 	}

		// 	if (!Evaluate.evaluate(condition, state)) {
		// 		visible = false;
		// 		break;
		// 	}
		// }

		return visible;
	}

	public static getCustomizationVariables(customization: CustomizationItemDto) {
		let variables = (customization.value as CustomizationOptionDto)?.variables;

		// Find variables in other places.
		switch (customization.type) {
			case CustomizationType.TEXT:
				if (customization.variableKey && customization.value) {
					variables = { [customization.variableKey]: customization.value };
				}
				break;

			case CustomizationType.SLIDER:
				// Support the single and range slider types.
				if (customization.variableKey) {
					if (customization.template === 'range') {
						variables = { [customization.variableKey]: customization.value?.[0] };
					} else {
						variables = { [customization.variableKey]: customization.value };
					}
				}

				if (customization.variableRangeKey) {
					variables = { [customization.variableRangeKey]: customization.value?.[1] };
				}
				break;

			case CustomizationType.IMAGE_PICKER:
			case CustomizationType.VIDEO_PICKER:
			case CustomizationType.AUDIO_PICKER:
				if (customization.mappings?.variables) {
					let value = customization.value;
					// Video and Audio have slightly different value structures.  The asset gets stored in a "file" property.
					if (customization.type === CustomizationType.VIDEO_PICKER || customization.type === CustomizationType.AUDIO_PICKER) {
						value = value?.file;
					}

					try {
						let variablesString = JSON.stringify(customization.mappings?.variables);
						variables = JSON.parse(MergeTags.applyMergeTagsToString(variablesString, value));
					} catch (e) {}
				}
				break;

			case CustomizationType.COLOR_PICKER:
				if (customization.mappings?.variables) {
					try {
						const variablesString = JSON.stringify(customization.mappings?.variables);
						variables = { ...JSON.parse(MergeTags.applyMergeTagsToString(variablesString, customization.value)), ...variables };
					} catch (e) {}
				}
				break;

			default:
				break;
		}

		return variables;
	}

	public static getCustomizationUnitVariables(customization: CustomizationItemDto, creativeUnit: CreativeUnitDto) {
		const value = customization.mappings || (customization.value as CustomizationOptionMappingDto);
		let foundUnit = null;

		if (value) {
			// Check if there are any creative overrides specifically for this creative unit within the select option.
			foundUnit = value.creativeUnits?.find(
				cu => cu.creativeUnitId === creativeUnit.creativeUnitId || cu.creativeUnitId === creativeUnit.id
			);
		}

		if (
			foundUnit &&
			customization?.value &&
			(customization.type === CustomizationType.VIDEO_PICKER || customization.type === CustomizationType.IMAGE_PICKER)
		) {
			if (foundUnit.variables && value) {
				let values = customization?.value;

				let variables = foundUnit.variables;

				if (customization.type === CustomizationType.VIDEO_PICKER || customization.type === CustomizationType.IMAGE_PICKER) {
					if (values?.cellDataObject) {
						values = {
							...values,
							...values.cellDataObject
						};
					}
				}
				try {
					if (values?.cellDataObject) {
						let varString = foundUnit.variables;

						let variablesString = JSON.stringify(varString);
						variables = JSON.parse(MergeTags.applyMergeTagsToString(variablesString, values));
						return variables;
					}
				} catch (e) {
					console.log('Error parsing variables', e);
				}
			}
		}

		return foundUnit?.variables;
	}

	/**
	 * Recursively find all of the customizations who should be visible based on the current conditions
	 * of the Creative Unit.
	 */
	public static getAllVisibleCustomizations(
		customizations: CustomizationItemDto[],
		unit?: CreativeUnitWithCustomizationsDto,
		additionalMergeState?: { [key: string]: any },
		editorMode?: boolean,
		activeGroup?: ProjectCartItemGroupDto
	): CustomizationItemDto[] {
		let hiddenGroups: string[] = [];

		// One merge state for the whole list, built on first use. Building it per
		// customization made a unit render cost customizations × (variables +
		// customizations) merge-tag evaluations — ~690k regex replaces per unit on a
		// 24-customization template, a multi-second freeze with 10+ units on screen.
		// Deferred rather than eager so a list whose customizations carry no
		// visibility conditions still costs nothing, as it did before (#11024).
		let unitState: { [key: string]: any } | undefined;
		let unitStateBuilt = false;
		const getUnitState = () => {
			if (!unitStateBuilt) {
				unitState = unit ? MergePropertiesUtils.getMergeableProperties(undefined, undefined, unit, activeGroup) : undefined;
				unitStateBuilt = true;
			}
			return unitState;
		};

		return CustomizationUtils.filterAllCustomizations(customizations, customization => {
			// If this customization has a parentId, we need to check if the parent is visible.
			if (customization.parentId) {
				if (hiddenGroups.includes(customization.parentId)) {
					return false;
				}
			}

			// Don't show this if we aren't in the editor and this is true.
			if (customization.onlyShowInEditor && !editorMode) {
				return false;
			}

			let isVisible = CustomizationUtils.isCustomizationVisibleWithState(customization, getUnitState, additionalMergeState);

			// If this is a group and it's not visible, we need to hide all of its children.
			if (customization.type === CustomizationType.GROUP && !isVisible) {
				hiddenGroups.push(customization.id);
			}

			return isVisible;
		});
	}

	/**
	 * Builds one customization tree for a sidebar rendered WITHOUT an active unit (the group tab and
	 * the "All" tab) out of the per-unit copies the cart holds.
	 *
	 * Every cart item carries a full copy of the package's customization tree, so any single copy is
	 * authoritative only for the rows that unit actually shows. A unit "owns" a customization when it
	 * is visible for that unit's own merge state — its variables and variableOverrides, its visibility
	 * conditions — and the owner's value is the one to display. Taking one item's copy wholesale (what
	 * the group object and the package seed used to do) renders every other unit's rows at the
	 * template defaults they were never edited away from.
	 *
	 * Only `value` and `dirty` are carried over. The pair travels together because `getDefaultValue`
	 * discards a non-dirty value in favour of the template default, and copying whole nodes would drag
	 * along the synthetic `parentId` that `findAllCustomizations` injects.
	 */
	public static mergeCustomizationValuesFromUnits(
		base: CustomizationItemDto[],
		units: CreativeUnitWithCustomizationsDto[],
		editorMode?: boolean
	): CustomizationItemDto[] {
		type ResolvedValue = { value: any; dirty: boolean };

		const unitsWithCustomizations = (units || []).filter(unit => unit?.customizations?.length);

		if (!base?.length || !unitsWithCustomizations.length) {
			return base;
		}

		// `dirty` is only ever read for truthiness, and the copies do not agree on which falsy flavour
		// they carry — undefined on one tree, false on another. Compared raw it marks identical rows as
		// divergent, which costs a full ownership sweep, and rebuilds nodes that did not change.
		const sameDirty = (a: boolean, b: boolean): boolean => !!a === !!b;

		// First unit holding each id at all — the fallback for rows no unit owns (group-only rows such
		// as messages) and the whole answer while the copies still agree.
		const firstSeen = new Map<string, ResolvedValue>();
		// First EDITED copy of each id, for those same unowned rows: an untouched copy that happens to
		// come first must not bury an edit, which is the whole point of resolving.
		const firstDirty = new Map<string, ResolvedValue>();
		const divergentIds = new Set<string>();
		const flattenedByUnit = unitsWithCustomizations.map(unit => ({
			unit,
			customizations: this.findAllCustomizations(unit.customizations)
		}));

		for (const { customizations } of flattenedByUnit) {
			for (const customization of customizations) {
				const seen = firstSeen.get(customization.id);

				if (!seen) {
					firstSeen.set(customization.id, { value: customization.value, dirty: customization.dirty });
				} else if (!isEqual(seen.value, customization.value) || !sameDirty(seen.dirty, customization.dirty)) {
					divergentIds.add(customization.id);
				}

				if (customization.dirty && !firstDirty.has(customization.id)) {
					firstDirty.set(customization.id, { value: customization.value, dirty: customization.dirty });
				}
			}
		}

		// Ownership only changes the answer where the copies disagree, and evaluating visibility
		// conditions per unit is the expensive half of this. Nothing has diverged yet on most cart
		// emissions, so skip that pass entirely until something has.
		const ownersById = new Map<string, ResolvedValue[]>();

		if (divergentIds.size) {
			for (const { unit, customizations } of flattenedByUnit) {
				const ownedIds = new Set(
					this.findAllCustomizations(
						this.getAllVisibleCustomizations(unit.customizations, unit, { activeCreativeUnit: unit }, editorMode)
					).map(customization => customization.id)
				);

				for (const customization of customizations) {
					if (!divergentIds.has(customization.id) || !ownedIds.has(customization.id)) {
						continue;
					}

					const owners = ownersById.get(customization.id) || [];
					owners.push({ value: customization.value, dirty: customization.dirty });
					ownersById.set(customization.id, owners);
				}
			}
		}

		const resolve = (id: string): ResolvedValue | undefined => {
			const owners = ownersById.get(id);

			// Several owners disagreeing is a genuine "multiple values" case, which the sidebar already
			// labels as such; prefer an edited value over an untouched one, then cart order.
			if (owners?.length) {
				return owners.find(owner => owner.dirty) || owners[0];
			}

			// Nothing owns this row. The per-unit pass only sees what a unit shows on its own, so rows
			// gated on group or listing metadata - and anything `onlyShowInEditor` - have no owner even
			// though the group and "All" sidebars render them. Prefer an edited copy over an untouched
			// one here too, or those rows keep showing the template default.
			return firstDirty.get(id) ?? firstSeen.get(id);
		};

		// Rebuilds only the nodes that actually change: the result is handed to OnPush sidebars and to
		// an Akita slice compared by reference, so a wholesale copy would emit on every activation.
		const mapTree = (nodes: CustomizationItemDto[]): CustomizationItemDto[] => {
			let changed = false;

			const mapped = nodes.map(node => {
				let next = node;

				if (node.type !== CustomizationType.GROUP) {
					const resolved = resolve(node.id);

					if (resolved && !(isEqual(resolved.value, node.value) && sameDirty(resolved.dirty, node.dirty))) {
						next = { ...node, value: resolved.value, dirty: resolved.dirty };
					}
				}

				const children = (next as CustomizationGroupDto).children;

				if (children?.length) {
					const mappedChildren = mapTree(children);

					if (mappedChildren !== children) {
						next = { ...next, children: mappedChildren } as CustomizationItemDto;
					}
				}

				if (next !== node) {
					changed = true;
				}

				return next;
			});

			return changed ? mapped : nodes;
		};

		return mapTree(base);
	}

	/**
	 * Collects distinct synthetic activeCreativeUnit merge states from ALL-tab cart preview
	 * entries (groups, plain listings, POIs) so metadata-gated customizations can be
	 * union-evaluated on the ALL tab. Group wrapper entries (identified by `items`) carry the
	 * raw group listing metadata (flat keys only) — exposed flat AND under `Group` for path
	 * parity with unit/group view. Standalone unit entries already carry flat + `Group`
	 * metadata from add-time, so they are used as-is. Synthetic units carry no `id`, which
	 * keeps DIMENSIONS customizations hidden via the guard in isCustomizationVisible.
	 */
	public static collectCartPreviewMetadataStates(previews: any[]): { activeCreativeUnit: { metadata: { [key: string]: any } } }[] {
		const states: { activeCreativeUnit: { metadata: { [key: string]: any } } }[] = [];
		const seen = new Set<string>();

		for (const preview of previews || []) {
			const meta = preview?.metadata;
			if (!meta || typeof meta !== 'object' || Array.isArray(meta) || Object.keys(meta).length === 0) {
				continue;
			}

			const metadata = preview.items ? { ...cloneDeep(meta), Group: cloneDeep(meta) } : cloneDeep(meta);

			let key: string;
			try {
				key = JSON.stringify(metadata);
			} catch {
				continue;
			}
			if (seen.has(key)) {
				continue;
			}
			seen.add(key);

			states.push({ activeCreativeUnit: { metadata } });
		}

		return states;
	}

	/**
	 * Union visibility across several merge-state contexts (an `undefined` entry is the base
	 * pass — identical to calling getAllVisibleCustomizations without additional state). A
	 * customization is returned when at least one pass shows it. Per-pass GROUP-hides-children
	 * semantics are preserved because every pass runs the full single-context filter; the
	 * union then re-filters the source tree by id, keeping original order and nesting.
	 */
	public static getAllVisibleCustomizationsUnion(
		customizations: CustomizationItemDto[],
		unit?: CreativeUnitWithCustomizationsDto,
		additionalMergeStates: ({ [key: string]: any } | undefined)[] = [],
		editorMode?: boolean,
		activeGroup?: ProjectCartItemGroupDto
	): CustomizationItemDto[] {
		if (additionalMergeStates.length <= 1) {
			return this.getAllVisibleCustomizations(customizations, unit, additionalMergeStates[0], editorMode, activeGroup);
		}

		const visibleIds = new Set<string>();
		for (const mergeState of additionalMergeStates) {
			const visible = this.getAllVisibleCustomizations(customizations, unit, mergeState, editorMode, activeGroup);
			for (const customization of this.findAllCustomizations(visible)) {
				visibleIds.add(customization.id);
			}
		}

		return this.filterAllCustomizations(customizations, (c: CustomizationItemDto) => visibleIds.has(c.id));
	}

	/**
	 * Same per-context passes as getAllVisibleCustomizationsUnion, plus per-row attribution:
	 * for each visible customization id, the indices of every metadata context (cart-preview
	 * order) under which it is visible, so callers can resolve its merge tags against all of
	 * them. The base pass (no additional context) always runs, keeping the visible set a
	 * strict superset of the single-pass behavior; rows visible only in the base pass get no
	 * attribution and callers fall back to the shared merge state.
	 */
	public static getAllVisibleCustomizationsWithContexts(
		customizations: CustomizationItemDto[],
		unit?: CreativeUnitWithCustomizationsDto,
		metadataContexts: { [key: string]: any }[] = [],
		editorMode?: boolean,
		activeGroup?: ProjectCartItemGroupDto
	): { visible: CustomizationItemDto[]; matchingContextIndicesById: { [id: string]: number[] } } {
		const visibleIds = new Set<string>();
		const matchingContextIndicesById: { [id: string]: number[] } = {};

		for (const customization of this.findAllCustomizations(
			this.getAllVisibleCustomizations(customizations, unit, undefined, editorMode, activeGroup)
		)) {
			visibleIds.add(customization.id);
		}

		for (let i = 0; i < metadataContexts.length; i++) {
			const visible = this.getAllVisibleCustomizations(customizations, unit, metadataContexts[i], editorMode, activeGroup);
			for (const customization of this.findAllCustomizations(visible)) {
				visibleIds.add(customization.id);
				if (!Object.prototype.hasOwnProperty.call(matchingContextIndicesById, customization.id)) {
					matchingContextIndicesById[customization.id] = [];
				}
				matchingContextIndicesById[customization.id].push(i);
			}
		}

		return {
			visible: this.filterAllCustomizations(customizations, (c: CustomizationItemDto) => visibleIds.has(c.id)),
			matchingContextIndicesById
		};
	}

	/**
	 * Combine several context metadata objects into one presentation state: plain objects
	 * merge recursively over the union of keys; a leaf keeps its single distinct value, or
	 * joins distinct primitive values with ', ' (cart order) when contexts disagree — so a
	 * merge tag in a row visible under several contexts renders every applicable value.
	 * Empty/null/undefined leaf values are skipped; on unjoinable type conflicts the first
	 * context wins. Inputs are never mutated.
	 */
	public static combineMetadataContexts(metadatas: { [key: string]: any }[]): { [key: string]: any } {
		const combineValues = (values: any[]): any => {
			if (values.length === 1) {
				return cloneDeep(values[0]);
			}
			if (values.length && values.every(v => v && typeof v === 'object' && !Array.isArray(v))) {
				const keys: string[] = Array.from(new Set(values.reduce((acc: string[], v) => acc.concat(Object.keys(v)), [])));
				const merged: { [key: string]: any } = {};
				for (const key of keys) {
					merged[key] = combineValues(values.filter(v => key in v).map(v => v[key]));
				}
				return merged;
			}
			const present = values.filter(v => v !== undefined && v !== null && v !== '');
			if (!present.length) {
				return cloneDeep(values[0]);
			}
			const distinct: any[] = [];
			for (const value of present) {
				if (!distinct.some(d => isEqual(d, value))) {
					distinct.push(value);
				}
			}
			if (distinct.length === 1) {
				return cloneDeep(distinct[0]);
			}
			if (distinct.every(v => typeof v !== 'object')) {
				// Template literal, not String(): this module imports a local `String` util that
				// shadows the global constructor.
				return distinct.map(v => `${v}`).join(', ');
			}
			return cloneDeep(distinct[0]);
		};

		const combined = combineValues(metadatas || []);
		return combined && typeof combined === 'object' && !Array.isArray(combined) ? combined : {};
	}

	/**
	 * Remove a customization from a creative unit.
	 */
	public static removeCustomization(
		creativeUnit: CreativeUnitWithCustomizationsDto,
		customizationId: string
	): CreativeUnitWithCustomizationsDto {
		// Remove the customization from the creative unit or from its parent customization.
		let newUnit = {
			...creativeUnit,
			customizations: creativeUnit.customizations
				.filter(c => c.id !== customizationId)
				.map(c => {
					if ((c as CustomizationGroupDto).children) {
						return {
							...c,
							children: (c as CustomizationGroupDto).children.filter(item => item.id !== customizationId)
						};
					}

					return c;
				})
		};

		return newUnit;
	}

	public static applyCustomizationConfig(customizations: CustomizationItemDto[], config: { [key: string]: string }) {
		return customizations.map(customization => {
			if (customization.type === CustomizationType.GROUP) {
				customization.children = this.applyCustomizationConfig(customization.children, config);
				return customization;
			}

			const value = config[customization.id];
			if (value === undefined) {
				return customization;
			}
			const customizationClass = getCustomizationClassByType(customization.type);

			return customizationClass?.applyStringValue(customization as any, value);
		});
	}

	public static applyCustomizationValueToLayer(
		value: any, // CustomizationItemDto['value']
		layer: LayerDto,
		creativeUnit?: CreativeUnitDto
	) {
		// Check for any layer overrides on this customization value.
		let newLayer = value?.layer?.id === layer?.id ? value.layer : (value?.mappings || value)?.layers?.find(l => l.id === layer.id);
		// console.log('Looking for Layer', layer, newLayer);

		// Next, check if there are any creative overrides specifically for this creative unit within the select option.
		// If so, deep override from the package level option overrides.

		// Clear any empty values out that we know we don't want to pass on.
		if (String.isEmpty(newLayer?.optimizations?.targetCrop?.th)) {
			newLayer = {
				...newLayer,
				optimizations: {
					...newLayer?.optimizations,
					targetCrop: {
						...newLayer?.optimizations?.targetCrop,
						th: undefined
					}
				}
			};
		}

		if (String.isEmpty(newLayer?.optimizations?.targetCrop?.tw)) {
			newLayer = {
				...newLayer,
				optimizations: {
					...newLayer?.optimizations,
					targetCrop: {
						...newLayer?.optimizations?.targetCrop,
						tw: undefined
					}
				}
			};
		}

		if (String.isEmpty(newLayer?.optimizations?.targetCrop?.tx)) {
			newLayer = {
				...newLayer,
				optimizations: {
					...newLayer?.optimizations,
					targetCrop: {
						...newLayer?.optimizations?.targetCrop,
						tx: undefined
					}
				}
			};
		}

		if (String.isEmpty(newLayer?.optimizations?.targetCrop?.ty)) {
			newLayer = {
				...newLayer,
				optimizations: {
					...newLayer?.optimizations,
					targetCrop: {
						...newLayer?.optimizations?.targetCrop,
						ty: undefined
					}
				}
			};
		}

		// Start by matching ids.  This will work in the editor but will fail in end user mode because the items
		// have generated ids
		let foundUnit = value?.creativeUnits?.find(cu => cu.id === creativeUnit.id);

		// If not, try to match the creative unit id, which should work for the end user mode.
		if (!foundUnit) {
			foundUnit = value?.creativeUnits?.find(cu => cu.creativeUnitId === creativeUnit.id);
		}
		if (!foundUnit) {
			foundUnit = value?.creativeUnits?.find(cu => cu.creativeUnitId === creativeUnit.creativeUnitId);
		}

		// If not, try to match the creative unit id again using the creativeUnitId field
		if (!foundUnit) {
			foundUnit = value?.creativeUnits?.find(cu => cu.creativeUnitId === creativeUnit.creativeUnitId);
		}

		// console.log('Looking for Unit', value.creativeUnits, creativeUnit.id, foundUnit);
		if (foundUnit) {
			// console.log('Found Unit', foundUnit, layer);
			const unitLayer = foundUnit.layers?.find(l => l.id === layer.id);
			if (unitLayer) {
				// console.log('Fount Unit Layer, Merging', newLayer, unitLayer);
				newLayer = merge(cloneDeep(newLayer || {}), unitLayer);
			}
		}

		if (newLayer) {
			// Special handling for objects to preserve content/assetPath and other properties if not overridden.
			if (newLayer.text) {
				newLayer = {
					...newLayer,
					text: this.preserveUnsetLayerValues<LayerTextDto>(layer.text, newLayer.text)
				};
			}

			if (newLayer.video) {
				newLayer = {
					...newLayer,
					video: this.preserveUnsetLayerValues<LayerVideoDto>(layer.video, newLayer.video)
				};
			}

			// ensure it's at least an empty object, because otherwise the merge may not clear out old values correctly
			if (!newLayer.style) {
				newLayer = {
					...newLayer,
					style: {}
				};
			}

			if (newLayer.assetPath || newLayer[layer.type]) {
				const mergedLayer = merge(cloneDeep(layer), newLayer);
				mergedLayer.assetPath = newLayer.assetPath;
				mergedLayer[layer.type] = newLayer[layer.type];
				return mergedLayer;
			}

			return merge(cloneDeep(layer), newLayer);
		} else {
			return layer;
		}
	}

	private static preserveUnsetLayerValues<T extends object>(base: T | undefined, override: T): T {
		const merged = { ...override };

		if (!base) {
			return merged;
		}

		for (const key of Object.keys(base) as Array<keyof T>) {
			const baseValue = base[key];
			const overrideValue: unknown = merged[key];

			if (baseValue !== undefined && (overrideValue === undefined || overrideValue === '')) {
				merged[key] = baseValue;
			}
		}

		return merged;
	}

	/**
	 * Remove any invalid layer references from the customizations by comparing against a list of layers.
	 * We expect the layers to already be flattened.
	 */
	public static removeInvalidLayerReferences(customizations: CustomizationItemDto[], layers: LayerDto[]) {
		function filterLayerRefs(property: any) {
			return property.filter((layer: any) => layers.some(l => l.id === layer.id)) || [];
		}

		return CustomizationUtils.mapAllCustomizations(customizations, c => {
			const newCustomization = ObjectUtils.unfreeze(c);

			if (c.layerIds?.length) {
				newCustomization.layerIds = c.layerIds.filter((id: string) => layers.some(l => l.id === id)) || [];
			}

			if (c.mappings?.layers?.length) {
				newCustomization.mappings.layers = filterLayerRefs(c.mappings.layers);
			}

			if (c.value?.layers?.length) {
				newCustomization.value.layers = filterLayerRefs(c.value.layers);
			}

			if (c.value?.mappings?.layers?.length) {
				newCustomization.value.mappings.layers = filterLayerRefs(c.value.mappings.layers);
			}

			if (c.options?.length) {
				newCustomization.options = c.options.map((option: any) => {
					if (option.layers?.length) {
						return {
							...option,
							layers: filterLayerRefs(option.layers)
						};
					} else if (option.layer) {
						return {
							...option,
							layer: layers.some(l => l.id === option.layer.id) ? option.layer : undefined
						};
					} else return option;
				});
			}

			return newCustomization;
		});
	}

	/**
	 * Discerns if the result of customizationClass?.getDefaultValue is wrapped and returns the appropriate values.
	 * @param result
	 * @param dirty
	 */
	public static unwrapDefaultCustomizationValue(result: any, dirty: boolean): any {
		let newValue: any;
		let newDirty: boolean;

		const isWrapped = typeof result === 'object' && result !== null && 'newValue' in result && 'dirty' in result;

		if (isWrapped) {
			newValue = result?.newValue ?? null;
			newDirty = result?.dirty ?? null;
		} else {
			newValue = result ?? null;
			newDirty = dirty;
		}

		return { value: newValue, dirty: newDirty };
	}

	/**
	 * Count all customizations including nested children
	 */
	public static countAllCustomizations(customizations: CustomizationItemDto[]): number {
		let count = 0;

		const countRecursive = (items: CustomizationItemDto[]) => {
			items?.forEach(item => {
				count++;
				if (item.type === CustomizationType.GROUP && (item as CustomizationGroupDto).children) {
					countRecursive((item as CustomizationGroupDto).children);
				}
			});
		};

		countRecursive(customizations);
		return count;
	}

	public static getIconByType(type: CustomizationType): string {
		return customizationIconsByType[type] || (type === 'group' ? 'layers' : 'text_snippet');
	}

	/**
	 * Whether the customization type is one of the media pickers (image, video, audio).
	 */
	public static isMediaCustomizationType(type?: CustomizationType | null): boolean {
		return !!type && [CustomizationType.IMAGE_PICKER, CustomizationType.VIDEO_PICKER, CustomizationType.AUDIO_PICKER].includes(type);
	}

	/**
	 * Create all possible variations based on the provided variationsById object.
	 * @param variationsById
	 */
	public static createVariations(variationsById: { [customizationId: string]: any[] }) {
		const customizationIds = Object.keys(variationsById);
		const values = customizationIds.map(id => variationsById[id] ?? []);

		return values.reduce<Record<string, any>[]>(
			(acc, arr, index) => {
				const customizationId = customizationIds[index];
				const newAcc: Record<string, any>[] = [];

				for (const base of acc) {
					for (const value of arr) {
						newAcc.push({
							...base,
							[customizationId]: value
						});
					}
				}
				return newAcc;
			},
			[{}]
		);
	}

	public static areVisibilityConditionsPresent(visibility: VisibilityConditionDto | VisibilityConditionDto[]) {
		if (!visibility) {
			return false;
		}

		if (Array.isArray(visibility)) {
			return visibility.length > 0;
		} else {
			return Object.keys(visibility).length > 0;
		}
	}

	/** Return all customization datasource relations
	 * output: slug array == string[]
	 * */
	public static getAllCustomizationDataSources(customizations: CustomizationItemDto[]): string[] {
		const allSlugs = this.flattenCustomizations(customizations)
			.map(customization => {
				switch (customization.type) {
					case CustomizationType.SELECT:
					case CustomizationType.COLOR_PICKER:
						return [customization?.dynamicLink?.dataSourceSlug];

					case CustomizationType.IMAGE_PICKER:
					case CustomizationType.VIDEO_PICKER:
					case CustomizationType.AUDIO_PICKER:
						return customization.dataSources;

					default:
						return null;
				}
			})
			.flat()
			.filter(Boolean);

		return [...new Set(allSlugs)];
	}
}
